import { useMemo, useState } from 'react'
import { PRIMARY } from '../data/demo'
import { Page } from '../components/Layout'
import { Metric } from '../components/ui'

type Preset = 'custom' | 'prop' | 'private'

const DEFAULTS = {
  ddBudget: 12,
  dailyLoss: 3,
  weeklyLoss: 6,
  leverage: 8,
  exposureConc: 35,
  ccyConc: 45,
  corrGroup: 40,
  winRate: PRIMARY.winRate,
  payoff: 1.45,
  tradesPerWeek: 12,
}

export function RiskPage() {
  const [preset, setPreset] = useState<Preset>('custom')
  const [ddBudget, setDdBudget] = useState(DEFAULTS.ddBudget)
  const [dailyLoss, setDailyLoss] = useState(DEFAULTS.dailyLoss)
  const [weeklyLoss, setWeeklyLoss] = useState(DEFAULTS.weeklyLoss)
  const [leverage, setLeverage] = useState(DEFAULTS.leverage)
  const [exposureConc, setExposureConc] = useState(DEFAULTS.exposureConc)
  const [ccyConc, setCcyConc] = useState(DEFAULTS.ccyConc)
  const [corrGroup, setCorrGroup] = useState(DEFAULTS.corrGroup)
  const [winRate, setWinRate] = useState(DEFAULTS.winRate)
  const [payoff, setPayoff] = useState(DEFAULTS.payoff)
  const [tradesPerWeek, setTradesPerWeek] = useState(DEFAULTS.tradesPerWeek)
  const [guardsOn, setGuardsOn] = useState(true)

  function applyPreset(p: Preset) {
    setPreset(p)
    if (p === 'prop') {
      setDdBudget(10)
      setDailyLoss(5)
      setWeeklyLoss(10)
      setLeverage(5)
      setExposureConc(30)
      setCcyConc(40)
      setCorrGroup(35)
    } else if (p === 'private') {
      setDdBudget(15)
      setDailyLoss(4)
      setWeeklyLoss(8)
      setLeverage(10)
      setExposureConc(40)
      setCcyConc(50)
      setCorrGroup(45)
    } else {
      setDdBudget(DEFAULTS.ddBudget)
      setDailyLoss(DEFAULTS.dailyLoss)
      setWeeklyLoss(DEFAULTS.weeklyLoss)
      setLeverage(DEFAULTS.leverage)
      setExposureConc(DEFAULTS.exposureConc)
      setCcyConc(DEFAULTS.ccyConc)
      setCorrGroup(DEFAULTS.corrGroup)
    }
  }

  function reset() {
    applyPreset('custom')
    setWinRate(DEFAULTS.winRate)
    setPayoff(DEFAULTS.payoff)
    setTradesPerWeek(DEFAULTS.tradesPerWeek)
    setGuardsOn(true)
  }

  const model = useMemo(() => {
    const p = winRate / 100
    const q = 1 - p
    // simplified risk of ruin under fixed fractional approx
    const edge = p * payoff - q
    const frac = Math.min(0.25, leverage / 100)
    let ror = 0
    if (edge <= 0) ror = 0.92
    else {
      const a = q / p / Math.max(payoff, 0.1)
      ror = Math.pow(a, Math.max(1, ddBudget / (frac * 100 * 0.4)))
      ror = Math.min(0.95, Math.max(0.02, ror))
    }

    const currentDd = PRIMARY.currentDrawdownPct
    const ddHeadroom = ddBudget - currentDd
    const dailyUsed = 1.1
    const weeklyUsed = 2.4

    const breaches: { rule: string; status: 'ok' | 'warn' | 'breach'; detail: string }[] = []
    const push = (rule: string, utilization: number, status: 'ok' | 'warn' | 'breach', detail: string) => {
      breaches.push({ rule, status, detail: `${detail} · util ${Math.round(utilization * 100)}%` })
    }

    push(
      'Drawdown budget',
      currentDd / ddBudget,
      currentDd > ddBudget ? 'breach' : currentDd > ddBudget * 0.7 ? 'warn' : 'ok',
      `${currentDd.toFixed(1)}% of ${ddBudget}%`,
    )
    push(
      'Daily loss guard',
      dailyUsed / dailyLoss,
      !guardsOn ? 'ok' : dailyUsed > dailyLoss ? 'breach' : dailyUsed > dailyLoss * 0.7 ? 'warn' : 'ok',
      guardsOn ? `${dailyUsed}% vs ${dailyLoss}% cap` : 'Guard disabled',
    )
    push(
      'Weekly loss guard',
      weeklyUsed / weeklyLoss,
      !guardsOn ? 'ok' : weeklyUsed > weeklyLoss ? 'breach' : weeklyUsed > weeklyLoss * 0.7 ? 'warn' : 'ok',
      guardsOn ? `${weeklyUsed}% vs ${weeklyLoss}% cap` : 'Guard disabled',
    )
    push(
      'Leverage ceiling',
      leverage / 15,
      leverage > 12 ? 'warn' : 'ok',
      `${leverage.toFixed(1)}× effective`,
    )
    push(
      'Symbol concentration',
      exposureConc / 50,
      exposureConc > 45 ? 'breach' : exposureConc > 35 ? 'warn' : 'ok',
      `Top symbol ${exposureConc}%`,
    )
    push(
      'Currency concentration',
      ccyConc / 60,
      ccyConc > 55 ? 'breach' : ccyConc > 45 ? 'warn' : 'ok',
      `Top CCY ${ccyConc}%`,
    )
    push(
      'Correlation group',
      corrGroup / 50,
      corrGroup > 45 ? 'warn' : 'ok',
      `Risk cluster ${corrGroup}%`,
    )

    const scores = [
      { name: 'Daily loss guard', score: dailyUsed / dailyLoss, note: 'Intraday stop physics' },
      { name: 'Drawdown budget', score: currentDd / ddBudget, note: 'Path to account break' },
      { name: 'Symbol concentration', score: exposureConc / 50, note: 'Single-name shock' },
      { name: 'Correlation group', score: corrGroup / 50, note: 'Book moves as one' },
      { name: 'Leverage', score: leverage / 15, note: 'Amplifies all of the above' },
    ].sort((a, b) => b.score - a.score)

    const stress = [
      {
        name: 'Gap −2R on top symbol',
        impact: `−${(exposureConc * 0.08).toFixed(1)}% equity`,
        breaks: exposureConc > 30 ? 'Symbol concentration first' : 'DD budget absorbs',
      },
      {
        name: 'USD spike / correlated FX book',
        impact: `−${(corrGroup * 0.07).toFixed(1)}% equity`,
        breaks: corrGroup > 40 ? 'Correlation group first' : 'Weekly guard',
      },
      {
        name: 'Three losing sessions streak',
        impact: `−${(dailyLoss * (guardsOn ? 0.9 : 1.6)).toFixed(1)}% path`,
        breaks: guardsOn ? 'Daily guard triggers' : 'DD budget unprotected',
      },
    ]

    return {
      edge: Math.round(edge * 1000) / 1000,
      ror: Math.round(ror * 1000) / 10,
      ddHeadroom,
      breaches,
      breaksFirst: scores[0]!,
      stress,
      weeklyRiskBudget: Math.round(weeklyLoss * PRIMARY.equity) / 100,
    }
  }, [
    winRate,
    payoff,
    ddBudget,
    dailyLoss,
    weeklyLoss,
    leverage,
    exposureConc,
    ccyConc,
    corrGroup,
    guardsOn,
  ])

  return (
    <Page wide>
      <div className="app-header">
        <div>
          <div className="chip-row" style={{ marginBottom: 8 }}>
            <span className="badge demo">DEMO analytics</span>
            <span className="badge warn">No trade execution</span>
          </div>
          <h1>Atlas Risk OS</h1>
          <p className="muted" style={{ margin: '0.35rem 0 0' }}>
            Budgets, guards, concentration, and ruin estimates — what breaks first?
          </p>
        </div>
        <button type="button" className="btn btn-secondary btn-sm" onClick={reset}>
          Reset state
        </button>
      </div>

      <div className="risk-layout">
        <aside className="card card-pad stack">
          <div>
            <div className="label" style={{ marginBottom: 6 }}>
              Presets
            </div>
            <div className="seg" role="group" aria-label="Risk presets">
              {(
                [
                  ['custom', 'Custom'],
                  ['prop', 'Prop firm'],
                  ['private', 'Private capital'],
                ] as const
              ).map(([id, label]) => (
                <button key={id} type="button" aria-pressed={preset === id} onClick={() => applyPreset(id)}>
                  {label}
                </button>
              ))}
            </div>
          </div>

          <label className="chip" style={{ justifyContent: 'space-between', display: 'flex' }}>
            <span>Loss guards armed</span>
            <input
              type="checkbox"
              checked={guardsOn}
              onChange={(e) => setGuardsOn(e.target.checked)}
              aria-label="Toggle loss guards"
            />
          </label>

          {(
            [
              ['Drawdown budget %', ddBudget, 3, 30, 0.5, setDdBudget],
              ['Daily loss guard %', dailyLoss, 1, 10, 0.5, setDailyLoss],
              ['Weekly loss guard %', weeklyLoss, 2, 20, 0.5, setWeeklyLoss],
              ['Effective leverage ×', leverage, 1, 20, 0.5, setLeverage],
              ['Top symbol exposure %', exposureConc, 10, 80, 1, setExposureConc],
              ['Top currency %', ccyConc, 10, 90, 1, setCcyConc],
              ['Correlation group %', corrGroup, 10, 80, 1, setCorrGroup],
              ['Win rate %', winRate, 30, 70, 0.5, setWinRate],
              ['Payoff ratio', payoff, 0.5, 3, 0.05, setPayoff],
              ['Trades / week', tradesPerWeek, 1, 40, 1, setTradesPerWeek],
            ] as const
          ).map(([label, val, min, max, step, set]) => (
            <div className="slider-row" key={label}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                <span>{label}</span>
                <span className="mono lime">{val}</span>
              </div>
              <input
                type="range"
                min={min}
                max={max}
                step={step}
                value={val}
                onChange={(e) => {
                  setPreset('custom')
                  set(Number(e.target.value))
                }}
                aria-label={label}
              />
            </div>
          ))}
        </aside>

        <div className="stack">
          <div className="grid-metrics">
            <div className="card card-pad">
              <Metric
                label="Risk of ruin est."
                value={`${model.ror}%`}
                tone={model.ror > 25 ? 'danger' : model.ror > 12 ? 'amber' : 'lime'}
                sub="simplified demo model"
              />
            </div>
            <div className="card card-pad">
              <Metric label="Edge / trade" value={model.edge.toFixed(3)} tone={model.edge > 0 ? 'lime' : 'danger'} />
            </div>
            <div className="card card-pad">
              <Metric
                label="DD headroom"
                value={`${model.ddHeadroom.toFixed(1)}%`}
                tone={model.ddHeadroom < 3 ? 'danger' : 'lime'}
              />
            </div>
            <div className="card card-pad">
              <Metric label="Weekly $ budget" value={`$${model.weeklyRiskBudget.toLocaleString()}`} />
            </div>
          </div>

          <div className="card card-pad">
            <h2 className="display" style={{ marginTop: 0, fontSize: '1.3rem' }}>
              What breaks first?
            </h2>
            <p className="notice warn" style={{ marginTop: 0 }}>
              <strong>{model.breaksFirst.name}</strong> — {model.breaksFirst.note}. Utilization score{' '}
              {(model.breaksFirst.score * 100).toFixed(0)}%.
            </p>
            <div className="split-2">
              {model.stress.map((s) => (
                <div key={s.name} className="panel" style={{ padding: '0.85rem' }}>
                  <strong>{s.name}</strong>
                  <div className="mono amber">{s.impact}</div>
                  <div className="muted">{s.breaks}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="card card-pad">
            <h2 className="display" style={{ marginTop: 0, fontSize: '1.3rem' }}>
              Rule board
            </h2>
            <div className="stack" style={{ gap: '0.55rem' }}>
              {model.breaches.map((b) => (
                <div
                  key={b.rule}
                  className={`notice ${b.status === 'ok' ? 'ok' : b.status === 'warn' ? 'warn' : 'danger'}`}
                >
                  <strong>{b.rule}</strong> · {b.status.toUpperCase()}
                  <div>{b.detail}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="notice">
            Risk OS is analytical only. It does not place, modify, or close trades. Estimates are educational
            approximations on DEMO inputs — not guarantees of survival or ruin.
          </div>
        </div>
      </div>
    </Page>
  )
}
